import { useState } from 'react';
import Button from '../Button';
import { submitQuoteRequest } from '../../services/api';

const INITIAL = {
  name: '',
  email: '',
  phone: '',
  company: '',
  quantity: '',
  message: '',
};

function validateEmail(val) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());
}

function validate(fields) {
  const errors = {};
  if (!fields.name.trim()) errors.name = 'Name is required.';
  if (!fields.email.trim()) {
    errors.email = 'Email is required.';
  } else if (!validateEmail(fields.email)) {
    errors.email = 'Enter a valid email address.';
  }
  if (fields.quantity !== '' && fields.quantity !== undefined) {
    const qty = Number(fields.quantity);
    if (!Number.isInteger(qty) || qty < 1) {
      errors.quantity = 'Quantity must be a whole number of at least 1.';
    }
  }
  return errors;
}

/**
 * QuoteForm
 *
 * Controlled form that posts to POST /quote-request.
 * Props:
 *   productId   — optional UUID of the product
 *   productName — optional display name shown as read-only context
 *   onSuccess   — optional callback after successful submission
 */
function QuoteForm({ productId, productName, onSuccess }) {
  const [fields, setFields] = useState(INITIAL);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  function handleChange(e) {
    const { name, value } = e.target;
    setFields((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  }

  function handleBlur(e) {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const fieldErrors = validate(fields);
    if (fieldErrors[name]) {
      setErrors((prev) => ({ ...prev, [name]: fieldErrors[name] }));
    } else {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setTouched({ name: true, email: true, quantity: true });
    const fieldErrors = validate(fields);
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    setLoading(true);
    setSubmitError('');

    // Build body — omit empty optional fields
    const body = {
      name: fields.name.trim(),
      email: fields.email.trim(),
    };
    if (productId) body.product_id = productId;
    if (fields.phone.trim()) body.phone = fields.phone.trim();
    if (fields.company.trim()) body.company = fields.company.trim();
    if (fields.quantity !== '') {
      const qty = Number(fields.quantity);
      if (!isNaN(qty) && qty >= 1) body.quantity = qty;
    }
    if (fields.message.trim()) body.message = fields.message.trim();

    try {
      await submitQuoteRequest(body);
      setSuccess(true);
      if (onSuccess) onSuccess();
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 429) {
        setSubmitError("You're submitting too fast — please wait a minute and try again.");
      } else if (status === 404) {
        setSubmitError('The requested product was not found. Please try again.');
      } else if (status === 400 && data?.message) {
        setSubmitError(data.message);
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <div className="form-success">
        <span className="form-success-icon" role="img" aria-label="Success">&#10003;</span>
        <h3>Quote Request Sent!</h3>
        <p>Thanks — your quote request has been sent. We'll be in touch shortly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      {/* Product context line (when opened from a product page) */}
      {productName && (
        <div className="form-context-line">
          Requesting a quote for: <em>{productName}</em>
        </div>
      )}

      {submitError && (
        <div className="form-error-banner" role="alert">
          {submitError}
        </div>
      )}

      {/* Row 1: Name + Email */}
      <div className="form-grid">
        <div className="form-row">
          <label className="field-label" htmlFor="qr-name">
            Name <span className="req">*</span>
          </label>
          <input
            id="qr-name"
            name="name"
            type="text"
            className={`field-input${errors.name ? ' field-invalid' : ''}`}
            value={fields.name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Jane Smith"
            autoComplete="name"
          />
          {errors.name && (
            <span className="field-error" role="alert">{errors.name}</span>
          )}
        </div>

        <div className="form-row">
          <label className="field-label" htmlFor="qr-email">
            Email <span className="req">*</span>
          </label>
          <input
            id="qr-email"
            name="email"
            type="email"
            className={`field-input${errors.email ? ' field-invalid' : ''}`}
            value={fields.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="jane@example.com"
            autoComplete="email"
          />
          {errors.email && (
            <span className="field-error" role="alert">{errors.email}</span>
          )}
        </div>
      </div>

      {/* Row 2: Phone + Company */}
      <div className="form-grid">
        <div className="form-row">
          <label className="field-label" htmlFor="qr-phone">
            Phone
          </label>
          <input
            id="qr-phone"
            name="phone"
            type="tel"
            className="field-input"
            value={fields.phone}
            onChange={handleChange}
            placeholder="(555) 000-0000"
            autoComplete="tel"
          />
        </div>

        <div className="form-row">
          <label className="field-label" htmlFor="qr-company">
            Company
          </label>
          <input
            id="qr-company"
            name="company"
            type="text"
            className="field-input"
            value={fields.company}
            onChange={handleChange}
            placeholder="Acme Retail LLC"
            autoComplete="organization"
          />
        </div>
      </div>

      {/* Row 3: Quantity */}
      <div className="form-row">
        <label className="field-label" htmlFor="qr-quantity">
          Quantity
        </label>
        <input
          id="qr-quantity"
          name="quantity"
          type="number"
          min="1"
          className={`field-input${errors.quantity ? ' field-invalid' : ''}`}
          value={fields.quantity}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="e.g. 100"
          style={{ maxWidth: 180 }}
        />
        {errors.quantity && (
          <span className="field-error" role="alert">{errors.quantity}</span>
        )}
      </div>

      {/* Row 4: Message */}
      <div className="form-row">
        <label className="field-label" htmlFor="qr-message">
          Message
        </label>
        <textarea
          id="qr-message"
          name="message"
          className="field-input"
          value={fields.message}
          onChange={handleChange}
          placeholder="Any specific requirements, variants, or questions…"
          rows={4}
        />
      </div>

      <div className="form-submit-row">
        <Button
          variant="primary"
          type="submit"
          disabled={loading}
          className={loading ? 'btn-submitting' : ''}
        >
          {loading ? 'Sending…' : 'Send Quote Request'}
        </Button>
      </div>
    </form>
  );
}

export default QuoteForm;
